import { z } from 'zod';

import {
  CONFIGURABLE_FIELDS,
  configurableFieldSchema,
  FIXED_FIELDS,
} from './constants';

/**
 * We only append the `fixed` field types to the parent `type` since
 * they are not configurable and are only added to support re-ordering
 * easily
 */

export const scoreSchema = z.coerce
  .number()
  .int({
    message: 'Decimals are not allowed',
  })
  .min(0)
  .max(10, 'Risk score must not be greater than 10.');

export const REPORT_FORM_FIELDS = {
  ...CONFIGURABLE_FIELDS,
  ...FIXED_FIELDS,
} as const;
export const fieldTypeSchema = z.nativeEnum(REPORT_FORM_FIELDS);
export type FieldTypeId = z.infer<typeof fieldTypeSchema>;

export const reportFormSchema = z.object({
  field_id: z.number().optional(),
  tenant: z.number().nullable().optional(),
  allow_multi_value: z.boolean(),
  has_custom_field_value: z.boolean(),
  is_default: z.boolean(),
  label: z.string().trim().min(1, 'Required'),
  is_required: z.boolean().default(false),
  allow_anonymous_submission: z.boolean().default(true),
  is_active: z.boolean(),
  enabled: z.boolean().default(true).optional(),
  options: z
    .array(
      z.object({
        value: z.string().trim().min(1, 'Required'),
        risk_score: scoreSchema,
      }),
    )
    .default([]),
  supports_conditional_logic: z.boolean().default(false).optional(),
  conditional_logic_activated: z.boolean().default(false),
  has_conditional_logic: z
    .object({
      matches_value: z.string(),
      label: z.string().trim(),
      type: configurableFieldSchema,
      report_custom_field: z.number().optional(),
      id: z.number().optional(),
      options: z
        .array(
          z.object({
            value: z.string().trim().min(1, 'Required'),
          }),
        ).optional(),
    })
    .nullable(),
  type: fieldTypeSchema,
  sequence: z.number(),
  help_text: z.string().nullable(),
});

export const reportFormConfigSchema = z.object({
  fields: z
    .array(reportFormSchema)
    .min(1)
    .refine(
      (data) => {
        const uniqueSet = new Set(data.map((field) => field.label));
        return uniqueSet.size === data.length;
      },
      {
        message: 'There are custom fields with duplicate labels.',
      },
    ),
});

export const conditionalLogicSchema = z
  .object({
    id: z.number().optional(),
    matches_value: z.string(),
    label: z.string(),
    type: configurableFieldSchema,
    report_custom_field: z.number().optional(),
    dropdown_choices: z.array(z.string()),
  })
  .nullable();

export const reportFormResponseSchema = z.object({
  id: z.number().optional(),
  tenant: z.number().nullable(),
  title: z.string(),
  dropdown_choices: z.array(z.string()),
  form_type: z.number(),
  sequence: z.number(),
  risk_score_choices: z.record(z.string(), z.number()).nullable(),
  allow_anonymous_submission: z.boolean().default(true),
  type: z.number(),
  is_required: z.boolean(),
  is_default: z.boolean(),
  has_custom_field_value: z.boolean(),
  custom_field_conditional_logic: conditionalLogicSchema,
  allow_multi_value: z.boolean(),
  is_active: z.boolean(),
  help_text: z.string().nullable(),
});

export const reportFormConfigResponseSchema = z.array(reportFormResponseSchema);

const flaggedDeleteSchema = z.object({
  id: z.number(),
});

export type FlaggedDeleteField = z.infer<typeof flaggedDeleteSchema>;

export type FormField = z.infer<typeof reportFormSchema>;
export type FormFields = z.infer<typeof reportFormConfigSchema>;

export type FormFieldResponse = z.infer<typeof reportFormResponseSchema>;
export type FormFieldResponseArray = z.infer<
  typeof reportFormConfigResponseSchema
>;

export type ConditionalLogic = z.infer<typeof conditionalLogicSchema>;

export type FormFieldUpdateRequest = {
  custom_fields: Omit<
    FormFieldResponse,
    'custom_field_conditional_logic' | 'tenant'
  >[];
  new_custom_fields: Omit<
    FormFieldResponse,
    'custom_field_conditional_logic' | 'tenant' | 'id'
  >[];
  to_be_deleted_custom_fields: FlaggedDeleteField[];
  conditional_logics: Omit<ConditionalLogic, 'report_custom_field'>[];
  new_conditional_logics: ConditionalLogic[];
  to_be_deleted_conditional_logics: FlaggedDeleteField[];
};
